import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import { withRouter, useLocation } from 'react-router-dom';
import { injectIntl, intlShape } from 'react-intl';
import { VerticalNav, Button, Icon } from 'patternfly-react';
import { routeConverter, hasAccess } from '@entando/utils';

import { clearAppTourProgress, setAppTourLastStep, setWizardEnabled } from 'state/app-tour/actions';

import { adminConsoleUrl } from 'helpers/urlUtils';
import { getPublicUrl } from 'helpers/resourcePath';
import { checkPermission } from 'helpers/permissions';
import UserMenuContainer from 'ui/internal-page/UserMenuContainer';
import LanguageSelectContainer from 'ui/internal-page/LanguageSelectContainer';

import {
  ROUTE_PAGE_TREE, ROUTE_FRAGMENT_LIST,
  ROUTE_PAGE_CONFIG, ROUTE_LABELS_AND_LANGUAGES, ROUTE_PAGE_TEMPLATE_LIST,
  ROUTE_RELOAD_CONFIG, ROUTE_DATABASE_LIST, ROUTE_FILE_BROWSER,
  ROUTE_PAGE_SETTINGS,
  // ROUTE_ECR_COMPONENT_LIST,
  ROUTE_DASHBOARD, ROUTE_USER_LIST, ROUTE_ROLE_LIST,
  ROUTE_GROUP_LIST, ROUTE_PROFILE_TYPE_LIST, ROUTE_USER_RESTRICTIONS, ROUTE_WIDGET_LIST,
  ROUTE_EMAIL_CONFIG,
} from 'app-init/router';

import HomePageLinkContainer from 'ui/internal-page/HomePageLinkContainer';
import {
  SUPERUSER_PERMISSION, MANAGE_PAGES_PERMISSION,
  EDIT_USER_PROFILES_PERMISSION, CRUD_USERS_PERMISSION,
  VIEW_USERS_AND_PROFILES_PERMISSION, CRUD_CONTENTS_PERMISSION,
  VALIDATE_CONTENTS_PERMISSION, MANAGE_RESOURCES_PERMISSION,
  MANAGE_CATEGORIES_PERMISSION,
  // ENTER_ECR_PERMISSION,
} from 'state/permissions/const';

import { withPermissionValues } from 'ui/auth/withPermissions';
import InfoMenu from 'ui/internal-page/InfoMenu';
// import getRuntimeEnv from 'helpers/getRuntimeEnv';
import { getRootPageCode } from 'state/pages/selectors';
import useLocalStorage from 'helpers/useLocalStorage';
import { getSystemReport } from 'state/system/selectors';
import { fetchSystemReport } from 'state/system/actions';
import { fetchRootPage } from 'state/pages/actions';
import { dismissedWizardKey } from 'ui/app-tour/constant';
import { getMfeTargetPrimaryMenu } from 'state/mfe/selectors';
import MfeContainer from 'ui/app/MfeContainer';
import { getAppTourlastStep } from 'state/app-tour/selectors';
// import { selectIsPrimaryTenant } from 'state/multi-tenancy/selectors';
import { selectCurrSystemConfigAdvancedSearch } from 'state/current-system-configuration/selectors';

const {
  Masthead, Item, SecondaryItem, Brand,
} = VerticalNav;

const publicUrl = getPublicUrl();

const getReportEntries = systemReport => (Array.isArray(systemReport) ? systemReport : []);

const getGroupItems = group =>
  (Array.isArray(group['appBuilderMenu.items']) ? group['appBuilderMenu.items'] : []);

const renderMenuItemsByHook = (systemReport, hook) =>
  getReportEntries(systemReport).filter(e => e['appBuilderMenu.hook'] === hook);

const renderDynamicItems = (items, intl, userPermissions) =>
  items
    .filter(item => checkPermission(item.requiredPermission, userPermissions))
    .map(item => (
      <SecondaryItem
        key={item.id}
        id={item.id}
        title={intl.formatMessage({
          id: item.labelId || item.id,
          defaultMessage: item.defaultLabel,
        })}
        href={adminConsoleUrl(item.href)}
      />
    ));

const renderLegacyPluginsMenu = (intl, userPermissions, systemReport) => {
  const pluginGroups = renderMenuItemsByHook(systemReport, 'legacyPlugins')
    .filter(g => getGroupItems(g)
      .some(item => checkPermission(item.requiredPermission, userPermissions)));

  if (pluginGroups.length === 0) return null;

  return (
    <Item
      id="menu-legacy-plugins"
      key="legacyPlugins"
      onClick={() => {}}
      iconClass="fa fa-puzzle-piece"
      title={intl.formatMessage({ id: 'menu.legacyPlugins', defaultMessage: 'Legacy Plugins' })}
    >
      {pluginGroups.flatMap((group) => {
        const items = getGroupItems(group)
          .filter(item => checkPermission(item.requiredPermission, userPermissions));
        const pluginId = group['appBuilderMenu.pluginId'] || '';
        const pluginLabel = group['appBuilderMenu.pluginLabel'] || pluginId;
        const header = pluginId ? [
          <SecondaryItem
            key={`header-${pluginId}`}
            id={`header-${pluginId}`}
            className="LegacyPlugins__group-header"
            title={pluginLabel}
          />,
        ] : [];
        return [
          ...header,
          ...renderDynamicItems(items, intl, userPermissions),
        ];
      })}
    </Item>
  );
};

const renderCmsMenuItems = (intl, userPermissions, systemReport, currSysConfigAdvancedSearchOn) => {
  const hasMenuContentsAccess = hasAccess([
    CRUD_CONTENTS_PERMISSION,
    VALIDATE_CONTENTS_PERMISSION,
  ], userPermissions);
  const hasMenuAssetsAccess = hasAccess([
    CRUD_CONTENTS_PERMISSION,
    VALIDATE_CONTENTS_PERMISSION,
    MANAGE_RESOURCES_PERMISSION,
  ], userPermissions);
  const hasVersioningAccess = hasMenuAssetsAccess;
  const hasMenuContentTypeAccess = hasAccess(SUPERUSER_PERMISSION, userPermissions);
  const hasMenuContentTemplatesAccess = hasAccess([
    SUPERUSER_PERMISSION, VALIDATE_CONTENTS_PERMISSION,
  ], userPermissions);
  const hasCategoriesAccess = hasAccess([
    SUPERUSER_PERMISSION, MANAGE_CATEGORIES_PERMISSION,
  ], userPermissions);
  const hasMenuContentSettingsAccess = hasAccess(SUPERUSER_PERMISSION, userPermissions);

  const cmsGroups = renderMenuItemsByHook(systemReport, 'cms');
  const cmsItems = cmsGroups.length > 0 ? getGroupItems(cmsGroups[0]) : [];

  return (
    <Item
      id="apps-cms"
      key="cms"
      onClick={() => { }}
      iconClass="VerticalMenuContainer_Icon Contents"
      title={intl.formatMessage({ id: 'menu.cms' })}
    >
      {
        hasMenuContentsAccess && (
          <SecondaryItem
            id="menu-contents"
            title={intl.formatMessage({ id: 'cms.menu.contents', defaultMessage: 'Management' })}
            href={adminConsoleUrl('do/jacms/Content/list.action')}
          />
        )
      }
      {
        hasMenuAssetsAccess && (
          <SecondaryItem
            id="menu-assets"
            title={intl.formatMessage({ id: 'cms.assets.title', defaultMessage: 'Assets' })}
            href={adminConsoleUrl('do/jacms/Resource/list.action?resourceTypeCode=Image')}
          />
        )
      }
      {
        hasMenuContentTemplatesAccess && (
          <SecondaryItem
            id="menu-content-template"
            title={intl.formatMessage({ id: 'cms.menu.contenttemplates', defaultMessage: 'Templates' })}
            href={adminConsoleUrl('do/jacms/ContentModel/list.action')}
          />
        )
      }
      {
        hasCategoriesAccess && (
          <SecondaryItem
            id="menu-category"
            title={intl.formatMessage({ id: 'menu.categories', defaultMessage: 'Categories' })}
            href={adminConsoleUrl('do/Category/viewTree.action')}
          />
        )
      }
      {
        hasVersioningAccess && (
          <SecondaryItem
            id="menu-versioning"
            title={intl.formatMessage({ id: 'menu.versioning', defaultMessage: 'Versioning' })}
            href={adminConsoleUrl('do/jpversioning/Content/Versioning/list.action')}
          />
        )
      }
      {renderDynamicItems(cmsItems, intl, userPermissions)}
      {
        hasMenuContentTypeAccess && (
          <SecondaryItem
            id="menu-content-type"
            title={intl.formatMessage({ id: 'cms.menu.contenttypes', defaultMessage: 'Types' })}
            href={adminConsoleUrl('do/Entity/initViewEntityTypes.action?entityManagerName=jacmsContentManager')}
          />
        )
      }
      {
        hasMenuContentSettingsAccess && (
          <SecondaryItem
            id="menu-content-settings"
            title={intl.formatMessage({ id: 'cms.menu.contentsettings', defaultMessage: 'Settings' })}
            href={adminConsoleUrl('do/jacms/Content/Admin/openIndexProspect.action')}
          />
        )
      }
      {
        currSysConfigAdvancedSearchOn && (
          <SecondaryItem
            id="menu-solar-config"
            title={intl.formatMessage({ id: 'cms.menu.solarConfig', defaultMessage: 'SOLR configuration' })}
            href={adminConsoleUrl('do/jpsolr/config')}
          />
        )
      }
    </Item>
  );
};

// const { COMPONENT_REPOSITORY_UI_ENABLED } = getRuntimeEnv();

// const renderComponentRepositoryMenuItem = (history, intl) => (
//   COMPONENT_REPOSITORY_UI_ENABLED ? (<Item
//     id="component-repository"
//     onClick={() => history.push(ROUTE_ECR_COMPONENT_LIST)}
//     iconClass="fa fa-cart-plus"
//     title={intl.formatMessage({ id: 'componentRepository.menuButton.title' })}
//   />) : '');

const getHeader = onStartTutorial => (
  <Masthead>
    <Brand
      href={`${publicUrl}${ROUTE_DASHBOARD}`}
      iconImg={`${publicUrl}/images/logo.svg`}
      img=""
      onClick={null}
    />
    <div className="VerticalMenu__headerContainer">
      <div id="header-breadcrumbs" className="VerticalMenu__headerBreadcrumbs" />
      <VerticalNav.IconBar collapse>
        <LanguageSelectContainer key="LanguageSelect" />
        <HomePageLinkContainer key="projectLink" />
        <InfoMenu key="InfoMenu" onStartTutorial={onStartTutorial} />
        <UserMenuContainer key="UserMenu" />
      </VerticalNav.IconBar>
    </div>
  </Masthead>);

const EntandoMenu = ({
  userPermissions, intl, history, onNextStep, onStartTutorial, onMount,
}) => {
  const [openPath, setOpenPath] = useState(null);
  const [collapsed, setCollapsed] = useLocalStorage('navCollapsed', false);
  const systemReport = useSelector(getSystemReport);
  const currSystemConfigAdvancedSearchOn = useSelector(selectCurrSystemConfigAdvancedSearch);
  const rootPageCode = useSelector(getRootPageCode);
  const menuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOpenPath(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openPath || openPath === '/') return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenPath(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPath]);

  const handleSecondaryCollapseBtnClick = () => {
    setOpenPath('/');
  };

  const handleExpandCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleItemClick = (item) => {
    if (item.subItems && item.subItems.length) {
      setOpenPath(`/${item.title}/`);
    }
  };

  return (
    <div ref={menuRef} className="safari-menu-fix VerticalMenuContainer">
      <VerticalNav
        blurDisabled
        dynamicBodyClasses
        forceHidden={false}
        hiddenIcons={false}
        hideMasthead={false}
        hoverDisabled
        onNavigate={({ href, onClick }) => {
          if (href) {
            window.location.href = href;
          } else {
            onClick();
          }
        }}
        pinnableMenus={false}
        hoverPath={openPath}
        onItemClick={handleItemClick}
        isMobile={false}
        navCollapsed={collapsed}
      >
        {getHeader(onStartTutorial)}
        <Item
          id="menu-dashboard"
          onClick={() => history.push(ROUTE_DASHBOARD)}
          iconClass="VerticalMenuContainer_Icon Dashboard"
          title={intl.formatMessage({ id: 'menu.dashboard', defaultMessage: 'Dashboard' })}
        />
        {
          hasAccess(MANAGE_PAGES_PERMISSION, userPermissions) && (
            <Item
              id="menu-page-creator"
              className="app-tour-step-3"
              onClick={() => onNextStep(4)}
              iconClass="VerticalMenuContainer_Icon Pages"
              title={intl.formatMessage({ id: 'menu.pageDesigner', defaultMessage: 'Pages' })}
            >
              <SecondaryItem
                id="menu-page-tree"
                title={intl.formatMessage({ id: 'menu.pageTree', defaultMessage: 'Management' })}
                className="app-tour-step-4"
                onClick={() => {
                  onNextStep(5);
                  history.push(ROUTE_PAGE_TREE);
                }}
              />
              <SecondaryItem
                id="menu-page-config"
                title={intl.formatMessage({ id: 'menu.pageConfig', defaultMessage: 'Designer' })}
                onClick={() =>
                  history.push(routeConverter(ROUTE_PAGE_CONFIG, { pageCode: rootPageCode }))
                }
              />
              {
                hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                  <SecondaryItem
                    id="menu-ux-pattern-page-templates"
                    title={intl.formatMessage({ id: 'menu.pageTemplates', defaultMessage: 'Templates' })}
                    onClick={() => history.push(ROUTE_PAGE_TEMPLATE_LIST)}
                  />
                )
              }
              {
                hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                  <SecondaryItem
                    id="menu-page-settings"
                    title={intl.formatMessage({ id: 'menu.pageSettings', defaultMessage: 'Settings' })}
                    onClick={() => history.push(ROUTE_PAGE_SETTINGS)}
                  />
                )
              }
            </Item>
          )
        }
        {
          hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
            <Item
              id="menu-ux-pattern"
              onClick={() => { }}
              iconClass="VerticalMenuContainer_Icon Components"
              title={intl.formatMessage({ id: 'menu.uxComponents', defaultMessage: 'Components' })}
            >
              <SecondaryItem
                id="menu-ux-pattern-widgets"
                title={intl.formatMessage({ id: 'menu.widget', defaultMessage: 'Widget' })}
                onClick={() => history.push(ROUTE_WIDGET_LIST)}
              />
              <SecondaryItem
                id="menu-ux-pattern-fragments"
                title={intl.formatMessage({ id: 'menu.fragments', defaultMessage: 'Fragments' })}
                onClick={() => history.push(ROUTE_FRAGMENT_LIST)}
              />
            </Item>
          )
        }
        {
          hasAccess([
            CRUD_CONTENTS_PERMISSION,
            MANAGE_RESOURCES_PERMISSION,
            MANAGE_CATEGORIES_PERMISSION,
            VALIDATE_CONTENTS_PERMISSION,
          ], userPermissions) &&
          renderCmsMenuItems(intl, userPermissions, systemReport, currSystemConfigAdvancedSearchOn)
        }
        {renderLegacyPluginsMenu(intl, userPermissions, systemReport)}
        {

          hasAccess(
            [
              VIEW_USERS_AND_PROFILES_PERMISSION,
              CRUD_USERS_PERMISSION,
              EDIT_USER_PROFILES_PERMISSION,
            ]
            , userPermissions,
          )
          && (
            <Item
              id="menu-user-settings"
              onClick={() => { }}
              iconClass="VerticalMenuContainer_Icon Users"
              title={intl.formatMessage({ id: 'menu.userSettings', defaultMessage: 'Users' })}
            >
              <SecondaryItem
                id="menu-users"
                title={intl.formatMessage({ id: 'menu.users', defaultMessage: 'Management' })}
                onClick={() => history.push(ROUTE_USER_LIST)}
              />
              {hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                <SecondaryItem
                  id="menu-roles"
                  title={intl.formatMessage({ id: 'menu.roles', defaultMessage: 'Roles' })}
                  onClick={() => history.push(ROUTE_ROLE_LIST)}
                />
              )}
              {hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                <SecondaryItem
                  id="menu-groups"
                  title={intl.formatMessage({ id: 'menu.groups', defaultMessage: 'Groups' })}
                  onClick={() => history.push(ROUTE_GROUP_LIST)}
                />
              )}
              {hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                <SecondaryItem
                  id="menu-profile"
                  title={intl.formatMessage({ id: 'menu.profileTypes', defaultMessage: 'Profile Types' })}
                  onClick={() => history.push(ROUTE_PROFILE_TYPE_LIST)}
                />
              )}
              {hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
                <SecondaryItem
                  id="menu-user-restrictions"
                  title={intl.formatMessage({ id: 'menu.users.restrictions', defaultMessage: 'Restrictions' })}
                  onClick={() => history.push(ROUTE_USER_RESTRICTIONS)}
                />
              )}
            </Item>
          )
        }

        {/* {
          (hasAccess(SUPERUSER_PERMISSION, userPermissions)
            || hasAccess(ENTER_ECR_PERMISSION, userPermissions))
          && renderComponentRepositoryMenuItem(history, intl)
        } */}
        {
          hasAccess(SUPERUSER_PERMISSION, userPermissions) && (
            <Item
              className="VerticalAdminConsoleMenu__fixed-bottom"
              id="menu-configuration"
              title={intl.formatMessage({ id: 'menu.settings', defaultMessage: 'Administration' })}
              onClick={() => { }}
              iconClass="fa fa-cogs"
            >
              <SecondaryItem
                id="menu-databases"
                title={intl.formatMessage({ id: 'menu.database', defaultMessage: 'Database' })}
                onClick={() => history.push(ROUTE_DATABASE_LIST)}
              />
              <SecondaryItem
                id="menu-labels-file-browser"
                title={intl.formatMessage({ id: 'menu.fileBrowser', defaultMessage: 'File Browser' })}
                onClick={() => history.push(ROUTE_FILE_BROWSER)}
              />
              <SecondaryItem
                id="menu-labels-languages"
                title={intl.formatMessage({ id: 'menu.labelsAndLanguages', defaultMessage: 'Languages & Labels' })}
                onClick={() => history.push(ROUTE_LABELS_AND_LANGUAGES)}
              />
              <SecondaryItem
                id="menu-email"
                title={intl.formatMessage({ id: 'menu.emailConfig', defaultMessage: 'Email Configuration' })}
                onClick={() => history.push(ROUTE_EMAIL_CONFIG)}
              />
              <SecondaryItem
                id="menu-reload-configuration"
                title={intl.formatMessage({ id: 'menu.reloadConfiguration', defaultMessage: 'Reload configuration' })}
                onClick={() => history.push(ROUTE_RELOAD_CONFIG)}
              />
            </Item>
          )
        }
        {/* add container to render MFE on side menu here */}
      </VerticalNav>
      {(openPath !== null && openPath !== '/') && (
        <Button className="VerticalMenu__secondary-collapse-btn" onClick={handleSecondaryCollapseBtnClick}>
          <Icon name="angle-double-left" />
        </Button>
      )}
      {(openPath === null || openPath === '/') && (
        <Button className="VerticalMenu__expand-collapse-btn" onClick={handleExpandCollapse}>
          <Icon name={`angle-double-${collapsed ? 'right' : 'left'}`} />
        </Button>
      )}
    </div>);
};

const MfeMenuContainer = ({
  menuId, headerId, onStartTutorial, appTourLastStep, onMount,
}) => {
  useEffect(() => {
    if (onMount) {
      onMount();
    }
  }, [onMount]);

  return (
    <div className="MfeMenuContainer">
      <div className="MfeMenuContainer__header-menu-container">
        {
        headerId ? <MfeContainer id={headerId} />
        : getHeader(onStartTutorial)
      }
      </div>
      {
        menuId && (
          <div
            className={`MfeMenuContainer__left-menu-container ${appTourLastStep === 3 || appTourLastStep === 4 ? 'tour-focus' : ''}`}
          >
            <MfeContainer id={menuId} />
          </div>
        )
      }
    </div>
  );
};

MfeMenuContainer.propTypes = {
  menuId: PropTypes.string.isRequired,
  headerId: PropTypes.string,
  onStartTutorial: PropTypes.func.isRequired,
  appTourLastStep: PropTypes.number.isRequired,
  onMount: PropTypes.func.isRequired,
};

MfeMenuContainer.defaultProps = {
  headerId: null,
};

const VerticalMenu = (props) => {
  const mfeMenu = useSelector(getMfeTargetPrimaryMenu);
  // const mfeHeaderMenu = useSelector(getMfeTargetPrimaryHeader);
  // const isPrimaryTenant = useSelector(selectIsPrimaryTenant);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchRootPage());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: remove when we have the ECR API is implemented
  const isMFEMenuEnabled = process.env.USE_MFE || false;

  return isMFEMenuEnabled
    ?
      <MfeMenuContainer
        menuId={mfeMenu.id}
        onStartTutorial={props.onStartTutorial}
        appTourLastStep={props.appTourLastStep}
        onMount={props.onMount}
      />
    : <EntandoMenu {...props} />;
};


EntandoMenu.propTypes = {
  intl: intlShape.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  userPermissions: PropTypes.arrayOf(PropTypes.string),
  onNextStep: PropTypes.func.isRequired,
  onStartTutorial: PropTypes.func.isRequired,
  onMount: PropTypes.func.isRequired,
};

EntandoMenu.defaultProps = {
  userPermissions: null,
};

VerticalMenu.propTypes = {
  intl: intlShape.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  userPermissions: PropTypes.arrayOf(PropTypes.string),
  appTourLastStep: PropTypes.number,
  onNextStep: PropTypes.func.isRequired,
  onStartTutorial: PropTypes.func.isRequired,
  onMount: PropTypes.func.isRequired,
};

VerticalMenu.defaultProps = {
  userPermissions: null,
  appTourLastStep: 1,
};

const mapStateToProps = state => ({
  appTourLastStep: getAppTourlastStep(state),
});

const mapDispatchToProps = (dispatch, { history }) => ({
  onNextStep: nextStep => dispatch(setAppTourLastStep(nextStep)),
  onStartTutorial: () => {
    sessionStorage.removeItem(dismissedWizardKey);
    history.push(ROUTE_DASHBOARD);
    dispatch(clearAppTourProgress());
    dispatch(setWizardEnabled(true));
    dispatch(setAppTourLastStep(1));
  },
  onMount: () => {
    dispatch(fetchSystemReport());
    dispatch(fetchRootPage());
  },
});

const VerticalMenuContainer =
  connect(mapStateToProps, mapDispatchToProps)(VerticalMenu);

export default withPermissionValues(injectIntl(withRouter(VerticalMenuContainer)));
